import MarkdownText from '@/components/markdown/markdown-text';
import { Box, Flex, Image, Section, Text, useBreakpointValue } from '@cmpsr/components';
import { CSSProperties } from 'react';
import 'swiper/css';
import 'swiper/css/pagination';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { IndustryProofsLight } from '../industry-proofs/industry-proofs-light';

const swiperStyles: { [key: string]: CSSProperties } = {
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '1.5rem',
    position: 'relative',
    bottom: '0',
  },
  bullet: {
    width: '8px',
    height: '8px',
    margin: '0 4px',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    borderRadius: '50%',
    display: 'inline-block',
    cursor: 'pointer',
  },
  bulletActive: {
    backgroundColor: 'white',
  },
};

function DemoCard({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <Flex
      flexDirection="column"
      backgroundColor="white"
      borderRadius="1rem"
      py={{ base: '0.5rem', lg: '2rem' }}
      px={{ base: '1rem', lg: '1.5rem' }}
      justifyContent="space-between"
      w={{ base: '300px', lg: '320px' }}
      h="100%"
    >
      <Flex flexDirection="column" mb="1rem">
        <Text color="darkBlue" fontSize="1.20988rem" fontWeight={700} lineHeight="130%" textAlign="start">
          {title}
        </Text>
        <MarkdownText color="darkBlue" fontSize="0.84694rem" fontWeight={400} lineHeight="150%" textAlign="start">
          {description}
        </MarkdownText>
      </Flex>
      <Flex justifyContent="center" alignItems="center" flex="1">
        {children}
      </Flex>
    </Flex>
  );
}

function DemoCarousel() {
  const slidesPerView = useBreakpointValue({ base: 1, md: 2, lg: 3 }, { ssr: false }) || 1;
  const showPagination = useBreakpointValue({ base: true, lg: false }, { ssr: false });

  return (
    <Box
      width="100%"
      maxWidth="1200px"
      sx={{
        '.swiper': {
          padding: '0 10px',
        },
        '.swiper-slide': {
          display: 'flex',
          justifyContent: 'center',
          height: 'auto',
        },
        '.swiper-wrapper': {
          alignItems: 'stretch',
        },
        '.swiper-pagination': {
          ...swiperStyles.pagination,
          display: showPagination ? 'flex' : 'none',
        },
        '.swiper-pagination-bullet': swiperStyles.bullet,
        '.swiper-pagination-bullet-active': swiperStyles.bulletActive,
        '.swiper-button-next, .swiper-button-prev': {
          display: 'none',
        },
        position: 'relative',
      }}
    >
      <Swiper
        slidesPerView={slidesPerView}
        spaceBetween={20}
        pagination={{
          clickable: true,
          dynamicBullets: false,
        }}
        modules={[Pagination]}
        className="demo-swiper"
      >
        <SwiperSlide>
          <DemoCard
            title="Learn your lifetime risk"
            description="Your lifetime risk analysis includes over 500+ risk factors for 21 major cancers"
          >
            <Image
              src="/images/info-images/example-risk-gauge.png"
              alt="Example lifetime risk gauge"
              maxHeight="200px"
              objectFit="contain"
            />
          </DemoCard>
        </SwiperSlide>
        <SwiperSlide>
          <DemoCard
            title="Build a personalized action plan"
            description="Get a personalized screening & action plan to reduce your risk"
          >
            <Image
              src="/images/demos/app-demo-action-plan.png"
              alt="App demo action plan"
              maxHeight="200px"
              objectFit="contain"
            />
          </DemoCard>
        </SwiperSlide>
        <SwiperSlide>
          <DemoCard title="Track your risk over time" description="Catch can reduce your cancer risk **by up to 60%**">
            <Image
              src="/images/info-images/track-risk-reduction.png"
              alt="Graph of risk reduction over time"
              maxHeight="200px"
              objectFit="contain"
            />
          </DemoCard>
        </SwiperSlide>
      </Swiper>
    </Box>
  );
}

export function DemoAndIndustryProofs() {
  return (
    <Section position="relative" borderRadius="2rem" overflow="hidden">
      <Box
        as="video"
        src="/videos/cells.mp4"
        autoPlay
        loop
        muted
        playsInline
        position="absolute"
        top="-10%"
        left="-5%"
        width="110%"
        height="120%"
        objectFit="cover"
        zIndex={0}
        style={{
          minHeight: '120%',
          minWidth: '110%',
        }}
      />
      <Box position="absolute" top={0} left={0} right={0} bottom={0} bg="rgba(0, 0, 0, 0.7)" zIndex={1} />
      <Box
        position="relative"
        zIndex={2}
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
      >
        <Flex
          data-testid="demo-and-industry-proofs-container"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={{ base: '1rem', lg: '2rem' }}
          py={{ base: '1rem', lg: '2rem' }}
          px={{ base: '1rem', lg: '2rem' }}
          maxW={{ base: '350px', md: '700px', lg: '1000px' }}
        >
          <DemoCarousel />
          <Text color="white" fontSize="1rem" fontWeight={600} lineHeight="130%" textAlign="center">
            Based on research from leading institutions
          </Text>
          <IndustryProofsLight />
        </Flex>
      </Box>
    </Section>
  );
}
